import { EditorState } from 'draft-js';
import { omit } from 'lodash';
import { UniqueIdentifier } from '@dnd-kit/core';

import { deleteFiles, uploadFiles } from '@qwealth/qvault';
import { TASK_PRIORITY_MEDIUM, TASK_TYPE } from '@qwealth/qdata';

import {
  BaseTaskTemplate,
  NewWorkflowTemplate,
  CRUMB_LABEL_ADD_TASK,
  CRUMB_LABEL_ADD_WORKFLOW,
  CRUMB_LABEL_EDIT_TASK,
  CRUMB_LABEL_EDIT_WORKFLOW,
  CRUMB_LABEL_WORKFLOW,
  CrumbOptions,
  DUE_DATE_OFFSET_PERIOD_DAYS,
  PAGE_ADD_TASK_TEMPLATE,
  PAGE_ADD_WORKFLOW_TEMPLATE,
  PAGE_EDIT_TASK_TEMPLATE,
  PAGE_EDIT_WORKFLOW_TEMPLATE,
  PAGE_MAIN_INFO,
  PAGE_MAIN_VIEW_WORKFLOW_TEMPLATE,
  PageOptions,
  TaskTemplateFormValues,
  WorkflowTemplate,
  WorkflowTemplateFormValues,
  WorkflowTemplateGroup,
  FROM_UNBLOCKED_GROUP_DATE,
} from 'data/models/WorkflowTemplates';
import { editorStateToJsonString, toEditorState } from './editorState';
import { resetSelectedWorkflowTemplate, resetWorkflowTemplateGroupForm } from 'data/actions/workflowTemplates';
import { AppThunkDispatch } from '../store';
import { FailedSettledResult, getRejectedSettledResults } from './promiseHelper';
import { getTagKeys } from './taskHelper';

export const crumbOptions = (page: PageOptions, currCrumbOptions: CrumbOptions[]): CrumbOptions[] => {
  switch (page) {
    case PAGE_MAIN_INFO:
      return [CRUMB_LABEL_WORKFLOW];
    case PAGE_MAIN_VIEW_WORKFLOW_TEMPLATE:
      return [CRUMB_LABEL_WORKFLOW];
    case PAGE_ADD_WORKFLOW_TEMPLATE:
      return [CRUMB_LABEL_WORKFLOW, CRUMB_LABEL_ADD_WORKFLOW];
    case PAGE_EDIT_WORKFLOW_TEMPLATE:
      return [CRUMB_LABEL_WORKFLOW, CRUMB_LABEL_EDIT_WORKFLOW];
    case PAGE_ADD_TASK_TEMPLATE:
      return [...currCrumbOptions, CRUMB_LABEL_ADD_TASK];
    case PAGE_EDIT_TASK_TEMPLATE:
      return [...currCrumbOptions, CRUMB_LABEL_EDIT_TASK];
    default:
      return [CRUMB_LABEL_WORKFLOW];
  }
};

export const getPageFromCrumb = (crumb: CrumbOptions): PageOptions => {
  switch (crumb) {
    case CRUMB_LABEL_WORKFLOW:
      return PAGE_MAIN_INFO;
    case CRUMB_LABEL_ADD_WORKFLOW:
      return PAGE_ADD_WORKFLOW_TEMPLATE;
    case CRUMB_LABEL_EDIT_WORKFLOW:
      return PAGE_EDIT_WORKFLOW_TEMPLATE;
    case CRUMB_LABEL_ADD_TASK:
      return PAGE_ADD_TASK_TEMPLATE;
    case CRUMB_LABEL_EDIT_TASK:
      return PAGE_EDIT_TASK_TEMPLATE;
    default:
      return PAGE_MAIN_INFO;
  }
};

export const DEFAULT_TASK_TEMPLATE: TaskTemplateFormValues = {
  title: '',
  taskType: TASK_TYPE.standard,
  editorState: EditorState.createEmpty(),
  isInternal: 1,
  priority: TASK_PRIORITY_MEDIUM,
  dueDateOptions: FROM_UNBLOCKED_GROUP_DATE,
  dueDateOffset: 1,
  dueDateOffsetPeriod: DUE_DATE_OFFSET_PERIOD_DAYS,
  assignee: null,
  tagKeys: [],
};

export const DEFAULT_WORKFLOW_TEMPLATE: WorkflowTemplateFormValues = {
  name: '',
  tagKeys: [],
  editorState: EditorState.createEmpty(),
  enableDueDate: false,
};

export const taskTemplateToFormValues = (taskTemplate: BaseTaskTemplate, files: File[]): TaskTemplateFormValues => {
  return omit(
    {
      ...taskTemplate,
      tagKeys: getTagKeys(taskTemplate.tags),
      editorState: toEditorState(taskTemplate.details),
      pending: files,
    },
    'details',
    'tags',
    'partnerId',
    'iaCode',
  );
};

export const workflowTemplateToFormValues = (workflowTemplate: WorkflowTemplate): WorkflowTemplateFormValues => {
  return omit(
    {
      ...workflowTemplate,
      tagKeys: getTagKeys(workflowTemplate.tags),
      editorState: toEditorState(workflowTemplate.description),
      enableDueDate: !!(workflowTemplate.dueDateOffset && workflowTemplate.dueDateOffsetPeriod),
    },
    'description',
    'tags',
    'partnerId',
    'iaCode',
    'groups',
  );
};

export function formValuesToTaskTemplate(formValues: TaskTemplateFormValues, partnerId: number, iaCode: string | null): BaseTaskTemplate {
  const newValues: BaseTaskTemplate = omit(
    {
      ...formValues,
      details: editorStateToJsonString(formValues.editorState),
      partnerId,
      iaCode,
      tags: [...formValues.tagKeys].map(tagKey => ({ partnerId: partnerId, key: tagKey })),
    },
    'editorState',
    'pending',
    'tagKeys',
  );

  return newValues;
}

type FormValuesToWorkflowTemplateReturn<T extends number | undefined> = T extends number ? WorkflowTemplate : NewWorkflowTemplate;

export const formValuesToWorkflowTemplate = <T extends number | undefined>(
  formValues: WorkflowTemplateFormValues,
  partnerId: number,
  iaCode: string | null,
  groups: WorkflowTemplateGroup[],
  id?: T,
): FormValuesToWorkflowTemplateReturn<T> => {
  const newGroups = groups.filter(group => group.tasksGroup.length > 0);

  const newValues = omit(
    {
      ...formValues,
      description: editorStateToJsonString(formValues.editorState),
      tags: [...formValues.tagKeys].map(tagKey => ({ partnerId: partnerId, key: tagKey })),
      partnerId,
      iaCode,
      groups: newGroups,
      dueDateOffset: formValues.enableDueDate ? formValues.dueDateOffset : null,
      dueDateOffsetPeriod: formValues.enableDueDate ? formValues.dueDateOffsetPeriod : null,
    },
    'editorState',
    'tagKeys',
  );

  if (id !== undefined) {
    return {
      ...newValues,
      id,
    } as FormValuesToWorkflowTemplateReturn<T>;
  }

  return newValues as FormValuesToWorkflowTemplateReturn<T>;
};

export const cleanupWorkflowState = (dispatch: AppThunkDispatch): void => {
  dispatch(resetWorkflowTemplateGroupForm());
  dispatch(resetSelectedWorkflowTemplate());
};

export const isDroppableContainerId = (id: UniqueIdentifier): boolean => {
  return typeof id === 'string' && id.startsWith('droppable-group-');
};

export const getDroppableContainerGroupIndex = (id: UniqueIdentifier): number | undefined => {
  if (isDroppableContainerId(id)) {
    const droppableContainerId = id as string;
    return Number(droppableContainerId.split('-').pop());
  }
  return undefined;
};

export const findGroupIndexById = (id: UniqueIdentifier, groups: WorkflowTemplateGroup[]): number | undefined => {
  const droppableContainerGroupIndex = getDroppableContainerGroupIndex(id);

  if (droppableContainerGroupIndex !== undefined) {
    return droppableContainerGroupIndex;
  }

  const index = groups.findIndex(group =>
    group.tasksGroup.some(taskTemplate => {
      const taskTemplateId = getTaskTemplateId(taskTemplate);
      return taskTemplateId === id;
    }),
  );
  return index === -1 ? undefined : index;
};

export const findTaskTemplateByIdWithinGroupsList = (
  id: UniqueIdentifier | undefined,
  groups: WorkflowTemplateGroup[],
): BaseTaskTemplate | undefined => {
  if (id === undefined) return undefined;

  for (const group of groups) {
    const taskTemplate = findTaskTemplateByIdWithinGroup(id, group);
    if (taskTemplate) {
      return taskTemplate;
    }
  }
  return undefined;
};

export const findTaskTemplateByIdWithinGroup = (id: UniqueIdentifier, group: WorkflowTemplateGroup): BaseTaskTemplate | undefined => {
  return group.tasksGroup.find(taskTemplate => {
    const taskTemplateId = getTaskTemplateId(taskTemplate);
    return taskTemplateId === id;
  });
};

export const findTaskTemplateCardIndexById = (id: UniqueIdentifier, group: WorkflowTemplateGroup): number | undefined => {
  const index = group.tasksGroup.findIndex(taskTemplate => {
    const taskTemplateId = getTaskTemplateId(taskTemplate);
    return taskTemplateId === id;
  });
  return index === -1 ? undefined : index;
};

export const getTaskTemplateAttachmentsPath = (partnerShortCode: string, taskTemplateId: number): string => {
  return `${partnerShortCode}/Internal/Task Template Attachments/${taskTemplateId}/`;
};

export const uploadTaskTemplateAttachments = async (partnerShortCode: string, taskTemplateId: number, files: File[]) => {
  const path = getTaskTemplateAttachmentsPath(partnerShortCode, taskTemplateId);
  await uploadFiles(files, path);
};

export const getTaskTemplateId = (taskTemplate: BaseTaskTemplate): string | number => {
  if (taskTemplate.id) {
    return taskTemplate.id;
  } else {
    return taskTemplate.temporaryUuid!;
  }
};

export const getTaskTemplatesFromGroups = (groups: WorkflowTemplateGroup[]): BaseTaskTemplate[] => {
  return groups.flatMap(group => group.tasksGroup);
};

export const deleteTaskTemplatesAttachments = async (taskTemplates: BaseTaskTemplate[]): Promise<FailedSettledResult[]> => {
  const results = await Promise.allSettled(
    taskTemplates.map(taskTemplate => (taskTemplate.attachmentsToDelete ? deleteFiles(taskTemplate.attachmentsToDelete) : Promise.resolve())),
  );

  return getRejectedSettledResults(results, taskTemplates);
};
